import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import TwilioNumbersTable from '../../components/admin/TwilioNumbersTable.jsx';
import PhoneNumberModule from '../../modules/phone-number/PhoneNumberModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';
import NumberAssignment from '../../components/admin/NumberAssignment.jsx';
import PhoneAssignmentManager from '../../components/admin/PhoneAssignmentManager.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Main phone number management component for admin interface
 */
const PhoneNumberManagement = () => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();

  // State management
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('overview'); // overview, assignments

  // Modal states
  const [showSearch, setShowSearch] = useState(false);
  const [showAssignment, setShowAssignment] = useState(false);
  const [numberForAssignment, setNumberForAssignment] = useState(null);

  // Drag and drop state
  const [draggedNumber, setDraggedNumber] = useState(null);
  const [dragOverParrot, setDragOverParrot] = useState(null);
  const [showDragDropZone, setShowDragDropZone] = useState(false);
  const [availableParrots, setAvailableParrots] = useState([]);
  const [loadingParrots, setLoadingParrots] = useState(false);

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  /**
   * Fetch all Twilio phone numbers
   */
  const fetchTwilioNumbers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      const response = await apiRequest('/api/admin/phone-numbers/all', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setTwilioNumbers(data.phoneNumbers || []);

    } catch (error) {
      console.error('Error fetching Twilio numbers:', error);
      setError(error.message || 'Failed to load phone numbers');
      showToast('Failed to load phone numbers', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  /**
   * Refresh phone numbers from Twilio
   */
  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      await fetchTwilioNumbers();
      showToast('Phone numbers refreshed successfully', 'success');
    } catch {
      // Error already handled in fetchTwilioNumbers
    } finally {
      setRefreshing(false);
    }
  }, [fetchTwilioNumbers, showToast]);

  /**
   * Handle number selection
   */
  const handleNumberSelect = useCallback((number) => {
    setSelectedNumber(number);
  }, []);

  /**
   * Handle number purchase
   */
  const handleNumberPurchased = useCallback((newNumber) => {
    setTwilioNumbers(prev => [...prev, newNumber]);
    showToast('Phone number purchased successfully', 'success');
  }, [showToast]);

  /**
   * Handle number assignment
   */
  const handleNumberAssigned = useCallback((updatedNumber) => {
    setTwilioNumbers(prev => prev.map(number =>
      number.id === updatedNumber.id ? updatedNumber : number
    ));
    if (selectedNumber?.id === updatedNumber.id) {
      setSelectedNumber(updatedNumber);
    }
    showToast('Phone number assigned successfully', 'success');
  }, [selectedNumber, showToast]);

  /**
   * Handle bulk assignment to parrot
   */
  const handleBulkAssign = useCallback(async (phoneNumberIds, parrotId, parrotName) => {
    try {
      const token = await getToken();

      // Assign each phone number to the parrot
      const assignmentPromises = phoneNumberIds.map(async (phoneNumberId) => {
        const response = await apiRequest('/api/phone/assign', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number_id: phoneNumberId,
            parrot_id: parrotId,
            replace_existing: true
          })
        });

        if (!response.ok) {
          const errorMessage = await parseApiError(response);
          throw new ApiError(errorMessage, response.status, response);
        }

        return response.json();
      });

      await Promise.all(assignmentPromises);

      // Update local state
      setTwilioNumbers(prev => prev.map(number => {
        if (phoneNumberIds.includes(number.id)) {
          return {
            ...number,
            assigned_parrot_id: parrotId,
            assigned_parrot_name: parrotName
          };
        }
        return number;
      }));

      showToast(`Successfully assigned ${phoneNumberIds.length} phone number${phoneNumberIds.length !== 1 ? 's' : ''} to ${parrotName}`, 'success');

    } catch (error) {
      console.error('Bulk assignment error:', error);
      showToast(error.message || 'Failed to assign phone numbers to parrot', 'error');
      throw error;
    }
  }, [getToken, showToast]);

  /**
   * Handle individual parrot assignment
   */
  const handleParrotAssign = useCallback(async (number, parrotId, parrotName) => {
    try {
      const token = await getToken();
      let userId = number.assigned_user_id; // Use existing user assignment

      // If assigning to a parrot, get the parrot details to find the user ID
      if (parrotId) {
        const parrotResponse = await apiRequest(`/api/admin/parrots/${parrotId}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!parrotResponse.ok) {
          const errorMessage = await parseApiError(parrotResponse);
          throw new ApiError(errorMessage, parrotResponse.status, parrotResponse);
        }

        const parrotData = await parrotResponse.json();
        userId = parrotData.user?.id;

        if (!userId) {
          throw new Error('Could not determine user for the selected parrot');
        }
      } else if (!userId) {
        throw new Error('Phone number must be assigned to a user first');
      }

      // Now assign/unassign the phone number using the admin endpoint
      const response = await apiRequest(`/api/admin/phone-numbers/${number.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId,
          parrotId: parrotId || null // null to unassign from parrot
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const assignmentData = await response.json();

      // Update local state with both user and parrot assignment
      const updatedNumber = {
        ...number,
        assigned_user_id: assignmentData.assignment.assignedUser.id,
        assigned_user_name: assignmentData.assignment.assignedUser.name,
        assigned_user_email: assignmentData.assignment.assignedUser.email,
        assigned_parrot_id: assignmentData.assignment.assignedParrot?.id || parrotId,
        assigned_parrot_name: assignmentData.assignment.assignedParrot?.name || parrotName,
        status: 'assigned'
      };

      setTwilioNumbers(prev => prev.map(n =>
        n.id === number.id ? updatedNumber : n
      ));

      if (selectedNumber?.id === number.id) {
        setSelectedNumber(updatedNumber);
      }

      showToast(`Phone number assigned to ${parrotName}`, 'success');

    } catch (error) {
      console.error('Parrot assignment error:', error);
      showToast(error.message || 'Failed to assign phone number to parrot', 'error');
      throw error;
    }
  }, [getToken, selectedNumber, showToast]);

  /**
   * Handle number unassignment
   */
  const handleNumberUnassign = useCallback(async (number) => {
    try {
      const token = await getToken();
      const response = await apiRequest(`/api/admin/phone-numbers/${number.id}/unassign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      // Update local state - mark as unassigned
      const updatedNumber = {
        ...number,
        assigned_user_id: null,
        assigned_user_name: null,
        assigned_user_email: null,
        assigned_parrot_name: null,
        status: 'unassigned'
      };

      setTwilioNumbers(prev => prev.map(n =>
        n.id === number.id ? updatedNumber : n
      ));

      if (selectedNumber?.id === number.id) {
        setSelectedNumber(updatedNumber);
      }

      showToast('Phone number unassigned successfully', 'success');

    } catch (error) {
      console.error('Error unassigning number:', error);
      showToast(error.message || 'Failed to unassign phone number', 'error');
    }
  }, [getToken, selectedNumber, showToast]);

  /**
   * Handle number release
   */
  const handleNumberRelease = useCallback(async (numberId) => {
    try {
      const token = await getToken();
      const response = await apiRequest(`/api/admin/phone-numbers/${numberId}/release`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      // Remove from local state
      setTwilioNumbers(prev => prev.filter(number => number.id !== numberId));
      if (selectedNumber?.id === numberId) {
        setSelectedNumber(null);
      }

      showToast('Phone number released successfully', 'success');

    } catch (error) {
      console.error('Error releasing number:', error);
      showToast(error.message || 'Failed to release phone number', 'error');
    }
  }, [getToken, selectedNumber, showToast]);

  /**
   * Fetch available parrots for drag and drop
   */
  const fetchAvailableParrots = useCallback(async () => {
    try {
      setLoadingParrots(true);
      const token = await getToken();
      const response = await apiRequest('/api/admin/parrots?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      setAvailableParrots(data.parrots || []);
    } catch (error) {
      console.error('Error fetching parrots:', error);
      showToast('Failed to load parrots', 'error');
    } finally {
      setLoadingParrots(false);
    }
  }, [getToken, showToast]);

  /**
   * Handle drag start
   */
  const handleDragStart = useCallback((e, number) => {
    if (!number.assigned_user_id) return; // Only allow dragging assigned numbers

    setDraggedNumber(number);
    setShowDragDropZone(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', number.id);
  }, []);

  /**
   * Handle drag end
   */
  const handleDragEnd = useCallback(() => {
    setDraggedNumber(null);
    setDragOverParrot(null);
    setShowDragDropZone(false);
  }, []);

  /**
   * Handle drag over parrot
   */
  const handleDragOver = useCallback((e, parrotId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverParrot(parrotId);
  }, []);

  /**
   * Handle drag leave parrot
   */
  const handleDragLeave = useCallback(() => {
    setDragOverParrot(null);
  }, []);

  /**
   * Handle drop on parrot
   */
  const handleDrop = useCallback((e, parrot) => {
    e.preventDefault();

    if (!draggedNumber) return;

    // Show confirmation dialog
    setConfirmationDialog({
      type: 'parrot_assignment',
      title: 'Assign Phone Number to Parrot',
      message: `Are you sure you want to assign ${draggedNumber.phone_number} to ${parrot.name}?`,
      onConfirm: () => {
        handleParrotAssign(draggedNumber, parrot.id, parrot.name);
        setConfirmationDialog(null);
      },
      onCancel: () => setConfirmationDialog(null)
    });

    handleDragEnd();
  }, [draggedNumber, handleParrotAssign, handleDragEnd]);

  /**
   * Open assignment modal
   */
  const openAssignmentModal = useCallback((number) => {
    setNumberForAssignment(number);
    setShowAssignment(true);
  }, []);

  // Load phone numbers and parrots on component mount
  useEffect(() => {
    fetchTwilioNumbers();
    fetchAvailableParrots();
  }, [fetchTwilioNumbers, fetchAvailableParrots]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Phone Number Management</h1>
          <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage Twilio phone numbers, assignments, and availability
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${theme === 'dark'
              ? 'bg-gray-600 text-white hover:bg-gray-700'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          {activeTab === 'overview' && (
            <button
              onClick={() => setShowSearch(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2 dark:text-white"
            >
              <span>🔍</span>
              <span>Search & Purchase</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
              ? 'border-teal-500 text-teal-600'
              : theme === 'dark'
                ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            📊 Overview & Management
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'assignments'
              ? 'border-teal-500 text-teal-600'
              : theme === 'dark'
                ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            🔧 Assignment Management
          </button>
        </nav>
      </div>

      {/* Error state */}
      {error && (
        <div className={`rounded-lg p-4 border ${theme === 'dark'
          ? 'bg-red-900/20 border-red-500/50'
          : 'bg-red-50 border-red-200'
          }`}>
          <div className="flex items-center">
            <span className={`mr-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>⚠️</span>
            <span className={theme === 'dark' ? 'text-red-300' : 'text-red-700'}>{error}</span>
            <button
              onClick={fetchTwilioNumbers}
              className={`ml-auto underline transition-colors ${theme === 'dark'
                ? 'text-red-400 hover:text-red-300'
                : 'text-red-600 hover:text-red-700'
                }`}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      {!loading && twilioNumbers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {twilioNumbers.length}
            </div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Numbers</div>
          </div>
          <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <div className="text-2xl font-bold text-teal-500">
              {twilioNumbers.filter(n => n.assigned_user_id).length}
            </div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Assigned</div>
          </div>
          <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <div className="text-2xl font-bold text-yellow-500">
              {twilioNumbers.filter(n => !n.assigned_user_id).length}
            </div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Available</div>
          </div>
          <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <div className="text-2xl font-bold text-blue-500">
              {twilioNumbers.filter(n => n.status === 'active').length}
            </div>
            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Active</div>
          </div>
        </div>
      )}

      {/* Drag and Drop Assignment Zone */}
      {showDragDropZone && (
        <div className={`rounded-lg border-2 border-dashed p-6 ${theme === 'dark'
          ? 'border-gray-600 bg-gray-800/50'
          : 'border-gray-300 bg-gray-50/50'
          }`}>
          <div className="text-center mb-4">
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Drag & Drop Assignment
            </h3>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Drop {draggedNumber?.phone_number} onto a Parrot to assign it
            </p>
          </div>

          {loadingParrots ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
              <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading parrots...
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableParrots.map((parrot) => (
                <div
                  key={parrot.id}
                  className={`p-4 rounded-lg border-2 border-dashed transition-all cursor-pointer ${dragOverParrot === parrot.id
                    ? theme === 'dark'
                      ? 'border-teal-400 bg-teal-900/20'
                      : 'border-teal-500 bg-teal-50'
                    : theme === 'dark'
                      ? 'border-gray-600 hover:border-gray-500 bg-gray-700'
                      : 'border-gray-300 hover:border-gray-400 bg-white'
                    }`}
                  onDragOver={(e) => handleDragOver(e, parrot.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, parrot)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">🦜</div>
                    <div>
                      <div className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {parrot.name}
                      </div>
                      <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {parrot.status || 'Active'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Phone numbers table */}
      <TwilioNumbersTable
        numbers={twilioNumbers}
        loading={loading}
        selectedNumber={selectedNumber}
        onNumberSelect={handleNumberSelect}
        onAssignNumber={(number, parrotId, parrotName) => {
          if (parrotId && parrotName) {
            // Direct parrot assignment
            handleParrotAssign(number, parrotId, parrotName);
          } else {
            // Open user assignment modal
            openAssignmentModal(number);
          }
        }}
        onUnassignNumber={handleNumberUnassign}
        onReleaseNumber={handleNumberRelease}
        onBulkAssign={handleBulkAssign}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />

      {/* Selected number details */}
      {selectedNumber && (
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Number Details: {selectedNumber.phone_number}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Status
              </label>
              <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${selectedNumber.assigned_user_id
                ? theme === 'dark' ? 'bg-teal-900/20 text-teal-400' : 'bg-teal-100 text-teal-800'
                : theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                }`}>
                {selectedNumber.assigned_user_id ? 'assigned' : 'unassigned'}
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Assigned User
              </label>
              <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {selectedNumber.assigned_user_name || 'Unassigned'}
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Assigned Parrot
              </label>
              <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {selectedNumber.assigned_parrot_name || 'None'}
              </div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Capabilities
              </label>
              <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                {selectedNumber.capabilities?.join(', ') || 'Voice, SMS'}
              </div>
            </div>
          </div>

          {selectedNumber.assigned_user_id && (
            <div className={`mt-4 pt-4 border-t flex flex-wrap gap-3 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => openAssignmentModal(selectedNumber)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors dark:text-white"
              >
                Reassign Number
              </button>
              {selectedNumber.assigned_parrot_name && (
                <button
                  onClick={() => handleParrotAssign(selectedNumber, null, null)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors dark:text-white"
                >
                  Unassign from Parrot
                </button>
              )}
              <button
                onClick={() => handleNumberUnassign(selectedNumber)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors dark:text-white"
              >
                Unassign Number
              </button>
            </div>
          )}
        </div>
      )}

      {/* Assignment Management Tab */}
      {activeTab === 'assignments' && (
        <PhoneAssignmentManager />
      )}

      {/* Modals */}
      {showSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg border w-full max-w-4xl max-h-[90vh] overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b flex justify-between items-center ${
              theme === 'dark' 
                ? 'bg-gray-900 border-gray-700' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div>
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Purchase Phone Numbers
                </h3>
                <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Search and purchase phone numbers for your organization
                </p>
              </div>
              <button
                onClick={() => setShowSearch(false)}
                className={`transition-colors ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <ModuleContextProvider>
                <PhoneNumberModule
                  config={{
                    context: 'admin',
                    searchFilters: [
                      { type: 'country', enabled: true, required: false, defaultValue: 'US' },
                      { type: 'area-code', enabled: true, required: false },
                      { type: 'city', enabled: true, required: false },
                      { type: 'region', enabled: true, required: false },
                      { type: 'pattern', enabled: true, required: false }
                    ],
                    purchaseFlow: 'confirmation',
                    showSkipOption: false,
                    maxResults: 20,
                    billingContext: {
                      source: 'admin',
                      category: 'phone-number-purchase'
                    },
                    onPurchaseComplete: (result) => {
                      console.log('Admin phone number purchased:', result);
                      if (!result.skipped) {
                        handleNumberPurchased(result);
                        setShowSearch(false);
                      }
                    }
                  }}
                  onEvent={(event) => {
                    console.log('Admin PhoneNumber event:', event);
                  }}
                />
              </ModuleContextProvider>
            </div>
          </div>
        </div>
      )}

      <NumberAssignment
        isOpen={showAssignment}
        number={numberForAssignment}
        onClose={() => {
          setShowAssignment(false);
          setNumberForAssignment(null);
        }}
        onNumberAssigned={handleNumberAssigned}
      />

      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg max-w-md w-full border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-100'
                    }`}>
                    <span className={`text-xl ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>❓</span>
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {confirmationDialog.title}
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {confirmationDialog.message}
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={confirmationDialog.onCancel}
                  className={`px-4 py-2 rounded-lg transition-colors ${theme === 'dark'
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmationDialog.onConfirm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors dark:text-white"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberManagement;
